/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, { useState, useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Card, Typography, CardContent, Button, TextField, CardHeader } from '@material-ui/core'
import AlertNotification from './AlertNotification'
import { forgotPassword } from '../services/AuthenticationService'
import ReCAPTCHA from 'react-google-recaptcha'
import SuccessCard from './SuccessCard'

const useCustomStyles = makeStyles((theme) => ({
  accountHeader: {
    fontSize: '1.75rem',
    lineHeight: '20px',
    color: theme.palette.bluePrimary.main,
    fontFamily: theme.typography.fontFamily,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  card: {
    [theme.breakpoints.down('xs')]: {
      height: '100%',
    },
  },
  cardHeader: {
    background: theme.palette.modalHeader,
    borderBottom: `1px solid ${theme.palette.modalGrayBorder}`,
    height: 82,
  },
  cardHolder: {
    width: 511,
    padding: theme.spacing(25, 0),
    margin: 'auto',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      width: '100%',
      minWidth: 0,
      padding: 0,
      '& .MuiPaper-rounded': {
        borderRadius: 0,
      },
    },
  },
  cardContent: {
    textAlign: 'left',
    justifyContent: 'center',
    display: 'flex',
    padding: theme.spacing(3, 0),
    '&.MuiCardContent-root:last-child': {
      paddingBottom: theme.spacing(4),
    },
    [theme.breakpoints.down('xs')]: {
      margin: 'auto',
      width: 320,
    },
  },
  boldText: {
    fontWeight: 'bold',
    fontSize: '1rem',
    marginBottom: theme.spacing(0.75),
  },
  button: {
    height: 40,
    width: 173,
    textTransform: 'none',
  },
  inputTextField: {
    width: 461,
    [theme.breakpoints.down('xs')]: {
      width: 320,
    },
  },
  inputTextFieldError: {
    width: 461,
    '& .MuiOutlinedInput-root': {
      border: `2px solid ${theme.palette.notificationError.main}`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    [theme.breakpoints.down('xs')]: {
      width: 320,
    },
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  formHeader: {
    marginBottom: theme.spacing(2),
    width: 461,
    [theme.breakpoints.down('xs')]: {
      width: 320,
    },
  },
  alertBox: {
    width: 461,
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      width: 320,
      margin: 'auto',
    },
  },
}))

/**
 * The footer.
 * @returns {Node} - A react Node.
 */
function ForgotPasswordCard() {
  const classes = useCustomStyles()
  const intl = useIntl()
  const emailRef = useRef()
  const captchaRef = useRef()
  const hostname = window && window.location && window.location.hostname
  const devDomains = RegExp(/(\.(dev|nit|nat)\.oclc\.org)|(localhost)/, 'g')

  const [captchaState] = useState({
    recaptchaSiteKey: devDomains.test(hostname)
      ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
      : '6LctDCscAAAAAIZLHotM7I8VkLdIRDll5WPsTSRw',
  })

  // prettier-ignore
  const messages = {
    accountInformation: intl.formatMessage({id: 'account_information', defaultMessage: 'Account information'}),
    emailLabel: intl.formatMessage({id: 'email_address', defaultMessage: 'E-mail address'}),
    enterEmail: intl.formatMessage({id: 'error_no_email_forgot', defaultMessage: 'Enter in format: yourname@example.com'}),
    enterOriginalEmail: intl.formatMessage({id: 'error_original_email', defaultMessage: 'Enter the original e-mail address you used to create your account.'}),
    enterRecaptcha: intl.formatMessage({id: 'error_no_recaptcha', defaultMessage: 'Verify that you are not a robot.'}),
    forgotExplanation: intl.formatMessage({id: 'forgot_explanation', defaultMessage: 'This service sends the username and a new, auto-generated password. The system will no longer accept your current password once you request a new password. You may reset your password once you sign in.'}),
    login: intl.formatMessage({id: 'success_login', defaultMessage: 'Login.'}),
    retryEmail: intl.formatMessage({id: 'error_retry_email', defaultMessage: 'Try Again. A system error occurred or email is not in our system.'}),
    sendPassword: intl.formatMessage({id: 'send_password', defaultMessage: 'Send Password'}),
    successCheckEmail: intl.formatMessage({id: 'success_check_email', defaultMessage: 'Check your email.'}),
    successEmailSent: intl.formatMessage({id: 'success_email_sent', defaultMessage: 'We emailed your username with a new password.'}),
  }

  const [values, setValues] = useState({
    email: '',
    token: '',
  })
  const [errors, setErrors] = useState({
    email: false,
    token: false,
  })
  const [showAlerts, setShowAlerts] = useState({
    retry: false,
  })
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)

  const verifyRecaptcha = async (response) => {
    setValues({ ...values, token: response })
    setErrors({ ...errors, token: false })
  }

  const submitForm = useCallback(
    async (e) => {
      e.preventDefault()
      setShowAlerts({ retry: false })
      if (!values.token) setErrors({ ...errors, token: true })
      if (!values.email) setErrors({ ...errors, email: true })

      if (values.email && values.token && isEmailValid(values.email)) {
        const response = await forgotPassword(values)
        if (response.status === 200 && response.message === 'Email sent') {
          setForgotPasswordSuccess(true)
        } else {
          setShowAlerts({ ...showAlerts, retry: true })
          setValues({ email: '', token: '' })
          emailRef.current.value = ''
          captchaRef.current.reset()
        }
      } else if (!isEmailValid(values.email)) {
        setErrors({ ...errors, email: true })
      }
    },
    [values.email, values.token],
  )

  const isEmailValid = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email))
  }

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
    if (event.target.value.length > 0) setErrors({ ...errors, [prop]: false })
  }

  const createAlert = (testId, text) => {
    return (
      <Box className={classes.alertBox}>
        <AlertNotification variant='error' id={testId}>
          {text}
        </AlertNotification>
      </Box>
    )
  }

  const createInputError = (testId, text) => {
    return (
      <AlertNotification variant='error' id={testId} alertLevel='inline'>
        {text}
      </AlertNotification>
    )
  }

  const retryAlert = createAlert('forgot-email-retry-alert', messages.retryEmail)
  const noEmailError = createInputError('no-email-error', messages.enterEmail)
  const noRecaptchaError = createInputError('no-recaptcha-error', messages.enterRecaptcha)

  const signInForm = (
    <>
      <form id='sign-in-form' data-testid='sign-in-form'>
        {showAlerts.retry ? <Box mb={2}>{retryAlert}</Box> : null}
        <Box className={classes.formHeader}>
          <Typography className={classes.boldText}>{messages.enterOriginalEmail}</Typography>
          <Typography>{messages.forgotExplanation}</Typography>
        </Box>
        <Box mb={2}>
          <Typography component='label' variant='body1' htmlFor='email-input' className={classes.label}>
            <strong>{messages.emailLabel}</strong>
          </Typography>
          <TextField
            inputRef={emailRef}
            className={errors.email ? classes.inputTextFieldError : classes.inputTextField}
            id='email-input'
            data-testid='email-input'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.email ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 256 }}
            onChange={handleChange('email')}
            type='email'
          />
          {errors.email ? noEmailError : null}
        </Box>
        <Box mb={2}>
          <ReCAPTCHA
            data-testid='create-account-recaptcha'
            ref={captchaRef}
            onChange={verifyRecaptcha}
            sitekey={captchaState.recaptchaSiteKey}
          />
          {errors.token ? noRecaptchaError : null}
        </Box>
        <Box>
          <Button
            color='primary'
            variant='contained'
            className={classes.button}
            type='submit'
            data-testid='forgot-password-cta'
            onClick={submitForm}>
            {messages.sendPassword}
          </Button>
        </Box>
      </form>
    </>
  )

  const cardHeaderTitle = (
    <Typography
      variant='h1'
      data-testid='forgot-password-card-header'
      underline='none'
      className={classes.accountHeader}>
      {messages.accountInformation}
    </Typography>
  )
  const successCard = (
    <SuccessCard
      title={messages.accountInformation}
      buttonText={messages.login}
      boldBodyText={messages.successCheckEmail}
      bodyText={messages.successEmailSent}
    />
  )

  return forgotPasswordSuccess ? (
    successCard
  ) : (
    <Box className={classes.cardHolder}>
      <Card elevation={9} className={classes.card}>
        <CardHeader className={classes.cardHeader} title={cardHeaderTitle} />
        <CardContent className={classes.cardContent}>{signInForm}</CardContent>
      </Card>
    </Box>
  )
}

export default ForgotPasswordCard
